/*
 OK FBI — Case Ranking Leaderboard
 Displays viewer scores accumulated across all cases in the session.
 Points: +100 correct culprit vote, +25 first-correct bonus, +50 streak bonus per consecutive case.
 Only revealed after the host closes the final vote (case_result exists AND leaderboard has been awarded).
*/
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Trophy, Star, Flame, RotateCcw, Crown } from "lucide-react";
import { sfx } from "@/lib/sounds";

export function useLeaderboard(pollMs = 5000) {
  const [rows, setRows] = useState([]);
  const [awarded, setAwarded] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/session/leaderboard");
      setRows(data.rows || []); setAwarded(data.awarded_cases || []);
    } catch { /* ignore 401/403 — leaderboard unavailable */ } finally { setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    if (!pollMs) return;
    const t = setInterval(load, pollMs);
    return () => clearInterval(t);
  }, [load, pollMs]);
  return { rows, awarded, loading, reload: load };
}

/** Trigger scoring after final accusation is submitted. Idempotent per case. */
export async function awardCaseScore(caseId) {
  try {
    const { data } = await api.post(`/session/case-score/${caseId}`);
    if (!data.already_awarded) sfx.caseSolved();
    return data;
  } catch (e) { return { ok: false, error: e.message }; }
}

export function LeaderboardPanel({ compact = false, title = "المحققون المتصدرون" }) {
  const { rows, reload } = useLeaderboard(6000);
  const reset = async () => { await api.post("/session/leaderboard/reset"); reload(); };
  return (
    <div className="panel p-4 border-metal" data-testid="leaderboard-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Trophy size={16} className="text-gold" />
          <div className="label-caps">{title}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9px] text-zinc-600 font-mono-ar">TOP INVESTIGATORS · {rows.length}</span>
          {!compact && rows.length > 0 && (
            <button onClick={reset} data-testid="leaderboard-reset" title="تصفير النقاط" className="text-zinc-500 hover:text-red-400 p-1"><RotateCcw size={11} /></button>
          )}
        </div>
      </div>
      {rows.length === 0 && <div className="text-[11px] text-zinc-500 text-center py-4">لم يُسجَّل أي متصدر بعد. سيظهرون هنا بعد حل القضية وإغلاق التصويت.</div>}
      <div className="space-y-1">
        {rows.slice(0, compact ? 5 : 20).map((r) => (
          <div key={r.name} className={`flex items-center gap-2 p-2 border rounded-sm text-xs ${r.rank === 1 ? "border-gold bg-[rgba(197,160,89,0.08)]" : "border-metal/60"}`}
            data-testid={`leaderboard-row-${r.rank}`}>
            <div className={`w-7 h-7 flex items-center justify-center border font-mono-ar text-sm ${r.rank === 1 ? "border-gold text-gold" : r.rank === 2 ? "border-zinc-400 text-zinc-300" : r.rank === 3 ? "border-amber-700 text-amber-500" : "border-metal text-zinc-500"}`}>
              {r.rank === 1 ? <Crown size={13} /> : `#${r.rank}`}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-cairo text-white truncate">{r.name}</div>
              <div className="text-[9px] text-zinc-500 flex gap-2">
                <span className="font-mono-ar">قضايا: {r.correct_cases?.length || 0}</span>
                {r.first_awards > 0 && <span className="text-amber-400 flex items-center gap-0.5"><Star size={9} /> {r.first_awards}</span>}
                {(r.correct_cases?.length || 0) >= 2 && <span className="text-red-400 flex items-center gap-0.5"><Flame size={9} /> streak</span>}
              </div>
            </div>
            <div className="text-gold font-mono-ar tabular-nums text-lg">{r.total_score}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
